// frontend/src/services/reports.js
// -----------------------------------------------------------------------------
// Service unique : rapports + assignations + équipes.
// Points clés :
//  - Normalisation toList(...)
//  - Assignation : on tente d'abord la route canonique
//      /quickbooks/custom-reports/:id/assign/,
//    puis on fait un fallback automatique sur /quickbooks/reports/:id/assign/
//  - Cas "liste vide" : certains backends refusent { user_ids: [] } -> on retente
//    avec { allow_empty: true, clear: true } pour clarifier l'intention.
// -----------------------------------------------------------------------------

import api from "api/axios";

/** Normalise n'importe quelle réponse en tableau. */
export function toList(data, keyHints = []) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.results)) return data.results;
  for (const k of keyHints) if (Array.isArray(data?.[k])) return data[k];
  for (const k of ["items", "budgets", "rows", "data", "list"]) {
    if (Array.isArray(data?.[k])) return data[k];
  }
  return [];
}

/* -------------------------------------------------------------------------- */
/*  Utils routes                                                              */
/* -------------------------------------------------------------------------- */
// ✅ Canonique d'abord
const ASSIGN_PRIMARY = (id) => `/quickbooks/custom-reports/${id}/assign/`;
// ♻️ Fallback legacy/unifié
const ASSIGN_FALLBACK = (id) => `/quickbooks/reports/${id}/assign/`;

/* -------------------------------------------------------------------------- */
/*  Profil courant                                                            */
/* -------------------------------------------------------------------------- */
export async function getMe() {
  const { data } = await api.get("/users/me/");
  return data;
}

/* -------------------------------------------------------------------------- */
/*  Rapports                                                                  */
/* -------------------------------------------------------------------------- */
export async function listCustomReportsAssignable() {
  try {
    const { data } = await api.get("/quickbooks/custom-reports/", {
      params: { assignable: 1 },
    });
    return toList(data);
  } catch {
    const { data } = await api.get("/quickbooks/custom-reports/");
    return toList(data);
  }
}

export async function listCustomReportsAll() {
  const { data } = await api.get("/quickbooks/custom-reports/");
  return toList(data);
}

/* -------------------------------------------------------------------------- */
/*  Assignations                                                              */
/* -------------------------------------------------------------------------- */
/** Lecture des IDs déjà assignés (primary -> fallback) */
export async function getReportAssignees(reportId) {
  // 1) tentative route canonique
  try {
    const { data } = await api.get(ASSIGN_PRIMARY(reportId));
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.assigned_user_ids)) return data.assigned_user_ids;
  } catch {
    // continue
  }
  // 2) fallback route "unifiée/legacy"
  try {
    const { data } = await api.get(ASSIGN_FALLBACK(reportId));
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.assigned_user_ids)) return data.assigned_user_ids;
  } catch {
    // nothing
  }
  return [];
}

/** Sauvegarde “bulk” des assignations (primary -> retry empty -> fallback -> retry empty) */
export async function saveReportAssignees(reportId, userIds) {
  const cleanIds = Array.isArray(userIds) ? userIds : [];
  const payload = { user_ids: cleanIds };

  // helper pour tenter un POST et, en cas de 400 + liste vide, retenter avec allow_empty
  const postWithEmptyAware = async (url) => {
    try {
      await api.post(url, payload);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 && cleanIds.length === 0) {
        // Certains backends demandent une intention explicite pour “vider”
        await api.post(url, { ...payload, allow_empty: true, clear: true });
      } else {
        throw err;
      }
    }
  };

  // 1) route canonique
  try {
    await postWithEmptyAware(ASSIGN_PRIMARY(reportId));
    return;
  } catch {
    // continue vers fallback
  }

  // 2) route fallback
  await postWithEmptyAware(ASSIGN_FALLBACK(reportId));
}

/* -------------------------------------------------------------------------- */
/*  Utilisateurs assignables                                                  */
/* -------------------------------------------------------------------------- */
export async function listAssignableUsers(reportId, { level, teamId } = {}) {
  try {
    const { data } = await api.get(`/quickbooks/custom-reports/${reportId}/assignable-users/`, {
      params: { level, team: teamId },
    });
    return toList(data);
  } catch {
    const { data } = await api.get("/users/list/", {
      params: { level, team: teamId },
    });
    return toList(data);
  }
}

export async function listAllUsers(params = {}) {
  const { data } = await api.get("/users/list/", { params });
  return toList(data);
}

/* -------------------------------------------------------------------------- */
/*  Teams                                                                     */
/* -------------------------------------------------------------------------- */
export async function listTeams() {
  const { data } = await api.get("/teams/");
  return toList(data);
}

/** Retourne les “memberships” : [{ id, user: {...}, role_level: "ED|GA|SUP|PD" }, ...] */
export async function getTeamMembers(teamId) {
  const { data } = await api.get(`/teams/${teamId}/members/`); // noter le / final
  return toList(data);
}

/* -------------------------------------------------------------------------- */
/*  Budgets                                                                   */
/* -------------------------------------------------------------------------- */
export async function listBudgets() {
  const { data } = await api.get("/quickbooks/budgets/");
  return toList(data, ["budgets"]);
}
